/**
 * PR3.6A review-list presentation only. Never modifies forecast hours or payroll amounts.
 * Stable staff keys distinguish people with the same name across different stores.
 */
export const forecastShiftCategory = shift => {
  if (shift?.kind === 'paid attendance') return 'paid';
  if (shift?.overnight) return 'overnight';
  if (Number(shift?.publicHours) > 0) return 'holiday';
  if (Number(shift?.sundayHours) > 0) return 'sunday';
  return 'ordinary';
};

export const forecastShiftCategoryLabel = shift => {
  const category = forecastShiftCategory(shift);
  if (category === 'paid') {
    const raw = String(shift?.shift || '').toLowerCase();
    return raw.includes('leave') ? 'Paid leave' : 'Paid attendance';
  }
  return {overnight:'Overnight · review', holiday:'Public holiday', sunday:'Sunday', ordinary:'Ordinary'}[category];
};

export const forecastShiftDisplay = shift => {
  const original = String(shift?.shift || '').trim();
  if (shift?.kind !== 'paid attendance' || !original.startsWith('ATTENDANCE::')) return original || '—';
  const code = original.slice('ATTENDANCE::'.length).split(':')[0]
    .replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  const title = code ? `${code.charAt(0).toUpperCase()}${code.slice(1)}` : 'Paid attendance';
  return `${title} · ${Number(shift?.hours || 0).toFixed(2)}h`;
};

export function buildForecastShiftReview(shifts = [], {staffKey = 'ALL', date = 'ALL', category = 'ALL', page = 1, pageSize = 20, storeLabels = {}} = {}) {
  const all = Array.isArray(shifts) ? shifts : [];
  const options = new Map();
  const dates = new Set();
  all.forEach(shift => {
    const key = String(shift.key || `${shift.storeId}__${shift.staffId}`);
    if (!options.has(key)) options.set(key, {
      key, name: String(shift.name || 'Staff member'),
      site: String(storeLabels[shift.storeId] || shift.storeId || ''), count: 0,
    });
    options.get(key).count++;
    if (shift.date) dates.add(shift.date);
  });
  const staffOptions = [...options.values()].sort((a,b) => a.name.localeCompare(b.name) || a.site.localeCompare(b.site));
  const dateOptions = [...dates].sort();
  const matched = all.filter(shift =>
    (staffKey === 'ALL' || shift.key === staffKey) &&
    (date === 'ALL' || shift.date === date) &&
    (category === 'ALL' || forecastShiftCategory(shift) === category)
  ).sort((a,b) => String(a.date).localeCompare(String(b.date)) || String(a.name).localeCompare(String(b.name)) || String(a.storeId).localeCompare(String(b.storeId)));
  const size = Number.isInteger(pageSize) && pageSize > 0 ? Math.min(pageSize, 100) : 20;
  const pages = Math.max(1, Math.ceil(matched.length / size));
  const requested = Number.isInteger(page) ? page : 1;
  const currentPage = Math.max(1, Math.min(requested, pages));
  const start = (currentPage - 1) * size;
  return {
    total: all.length,
    matched: matched.length,
    hours: Math.round(matched.reduce((total, shift) => total + (Number(shift.hours) || 0), 0) * 100) / 100,
    staffOptions, dateOptions,
    pages, page: currentPage,
    from: matched.length ? start + 1 : 0,
    to: Math.min(start + size, matched.length),
    rows: matched.slice(start, start + size),
  };
}
